import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@app/core/services/auth.service';
import { UserService } from '@app/core/services/user.service';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-callback',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="min-h-screen bg-gradient-to-br from-red-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div class="max-w-md w-full">
        <div *ngIf="error" class="bg-red-50 text-red-800 rounded-lg p-4 mb-4">
          <h3 class="text-lg font-semibold">Authentication Error</h3>
          <p class="text-sm">{{ error }}</p>
          <button
            (click)="goHome()"
            class="mt-2 text-sm font-medium text-red-800 hover:text-red-900"
          >
            Return Home
          </button>
        </div>

        <div *ngIf="!error" class="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-gray-100">
          <div class="text-center space-y-4">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <h2 class="text-xl font-semibold">Authenticating...</h2>
            <p class="text-sm text-gray-600">
              Please wait while we complete the authentication process.
            </p>
          </div>
        </div>
      </div>
    </div>
  `
})
export class CallbackComponent implements OnInit {
  protected error: string | null = null;
  private defaultRedirectPath = '/question'; // Adjust if needed

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    const code = this.route.snapshot.queryParamMap.get('code');
    const state = this.route.snapshot.queryParamMap.get('state');
    const error = this.route.snapshot.queryParamMap.get('error');
    const errorDescription = this.route.snapshot.queryParamMap.get('error_description');

    if (error) {
      this.error = errorDescription || 'Authentication failed';
      return;
    }

    if (!code || !state) {
      this.error = 'Invalid callback URL';
      return;
    }

    this.authService.handleCallback(code, state).pipe(
      switchMap(() => {
        // Call ping after successful authentication to get user info
        return this.userService.ping();
      })
    ).subscribe({
      next: (userInfo) => {
        console.log('User authenticated and info loaded:', userInfo);
        const redirectPath = this.authService.getRedirectPath() || this.defaultRedirectPath;
        this.router.navigateByUrl(redirectPath);
        this.authService.clearRedirectPath();
      },
      error: (err) => {
        this.error = err.message || 'Authentication failed';
        console.error('Authentication error:', err);
      }
    });
  }

  protected goHome(): void {
    this.router.navigate(['/']);
  }
}
